import { original } from '@reduxjs/toolkit';
import {
  EvictionRiskMappingPerDistrict,
  RiskBand,
  RiskByYearMonthDistrict,
  ReportFilters,
  MonthAbbrev,
  MONTH_ORDER
} from '../../../types/types';

/**
 * Risk-band thresholds, evaluated against total_individuals_at_risk (per
 * your confirmation — households produced a materially different result
 * for districts like Grasbaaleey, so this must stay individuals-based
 * unless you tell me otherwise).
 *
 * extreme: > 2500 · high: > 1500 · medium: > 1000 · low: <= 1000
 */
export function getRiskBand(individualsAtRisk: number): RiskBand {
  if (individualsAtRisk > 2500) return 'extreme';
  if (individualsAtRisk > 1500) return 'high';
  if (individualsAtRisk > 1000) return 'medium';
  return 'low';
}

export interface DistrictRiskEntry {
  district: string;
  individualsAtRisk: number;
  householdsAtRisk: number;
  band: RiskBand;
}

export interface AreaRiskMetrics {
  /** Districts with any recorded risk (individualsAtRisk > 0) — a district at 0 isn't "at risk". */
  districtsAtRiskCount: number;
  bandCounts: Record<RiskBand, number>;
  /** Sum across every district, regardless of band — used for the placeholder third card. */
  totalHouseholdsAtRisk: number;
  totalIndividualsAtRisk: number;
  /** Top 5 districts by individualsAtRisk, each carrying its own real band
   * (not assumed to all be "extreme" — with few districts in a real dataset,
   * the top 5 may span multiple bands). */
  top5: DistrictRiskEntry[];
}

const MAX_TOP_DISTRICTS = 5;

export interface ResolvedRiskSnapshot {
  snapshot: EvictionRiskMappingPerDistrict;
  /** The month actually used — may differ from filters.month (see below). */
  resolvedMonth: MonthAbbrev | null;
  /** True when filters.mode === 'year' and no month was selected, so we
   * fell back to the latest available month instead of the user's pick. */
  firstMonth: MonthAbbrev | null;
  /** Distinguishes "nothing recorded for this year at all" from "this
   * year has data, just not for the specific month selected" — these
   * need different messaging, not the same blanket "no data" line. */
  reason: 'ok' | 'no-year-data' | 'no-month-data';
}

/**
 * Resolves which single month's snapshot to show for the given filters.
 *
 * This is NOT the same rule as eviction numbers: households "at risk" is a
 * stock, not a flow, so there is no meaningful way to "sum year-to-date."
 * A specific month always means "read that month directly." The only
 * ambiguous case is `mode: 'year'` with no month chosen — there we fall
 * back to the most recent month on record within that year, since showing
 * nothing would be worse than showing the latest known snapshot.
 */
export function resolveRiskSnapshot(
  data: RiskByYearMonthDistrict,
  filters: ReportFilters,
  now: Date = new Date(),
): ResolvedRiskSnapshot {
  const yearData = data[String(filters.year)];

  console.log('MONTH_ORDER', MONTH_ORDER);
  console.log('yearData keys', Object.keys(yearData ?? {}));
  console.log('now', now.toISOString(), now.getMonth());
  if (!yearData) {
    return { snapshot: {}, resolvedMonth: null, firstMonth: null, reason: 'no-year-data' };
  }

  const lastAllowedIndex = filters.year === now.getFullYear() ? now.getMonth() : MONTH_ORDER.length - 1;

  const availableMonths = MONTH_ORDER.filter(
    (m, i) => i <= lastAllowedIndex && yearData[m],
  );

  const latest = availableMonths[availableMonths.length - 1] ?? null;
  if (!latest) {
    return { snapshot: {}, resolvedMonth: null, firstMonth: null, reason: 'no-year-data' };
  }
  return {
    snapshot: yearData[latest] ?? {},
    resolvedMonth: latest,
    firstMonth: availableMonths[0],
    reason: 'ok',
  };
}

const AGGREGATION: 'sum' | 'max' | 'latest' = 'sum';
type DistrictRiskStats = EvictionRiskMappingPerDistrict[string];

export function resolveYearRiskSnapshot(
  data: RiskByYearMonthDistrict,
  filters: ReportFilters,
  now: Date = new Date(),
): EvictionRiskMappingPerDistrict {
  const yearData = data[String(filters.year)];

  if (!yearData) return {};

  const lastAllowedIndex = filters.year === now.getFullYear() ? now.getMonth() : MONTH_ORDER.length - 1;

  const merged: EvictionRiskMappingPerDistrict = {};
  const areasByDistrict = new Map<string, Map<string, string>>();

  MONTH_ORDER.forEach((m, i) => {
    if (i > lastAllowedIndex) return;
    const monthSnapshot = yearData[m];
    if (!monthSnapshot) return;

    for (const [district, stats] of Object.entries(monthSnapshot)) {
      const prev: DistrictRiskStats | undefined = merged[district];
      const individuals = stats.total_individuals_at_risk;
      const households = stats.total_households_at_risk;

      let nextIndividuals = individuals;
      let nextHouseholds = households;
      if (prev) {
        if (AGGREGATION === 'sum') {
          nextIndividuals = prev.total_individuals_at_risk + individuals;
          nextHouseholds = prev.total_households_at_risk + households;
        } else if (AGGREGATION === 'max') {
          nextIndividuals = Math.max(prev.total_individuals_at_risk, individuals);
          nextHouseholds = Math.max(prev.total_households_at_risk, households);
        }
      }
      merged[district] = {
        ...stats,
        total_individuals_at_risk: nextIndividuals,
        total_households_at_risk: nextHouseholds,
      };

      // Union catchment areas across all months, deduplicated (case/space-insensitive)
      let seen = areasByDistrict.get(district);
      if (!seen) {
        seen = new Map();
        areasByDistrict.set(district, seen);
      }
      for (const area of stats.catchment_areas_at_risk ?? []) {
        const key = area.trim().toLowerCase();
        if (key && !seen.has(key)) seen.set(key, area.trim());
      }
    }
  });

  areasByDistrict.forEach((seen, district) => {
    const areas: string[] = [];
    seen.forEach((original) => areas.push(original));
    merged[district] = { ...merged[district], catchment_areas_at_risk: areas };
  });
  return merged;
}

export function computeAreaRiskMetrics(
  data: EvictionRiskMappingPerDistrict,
): AreaRiskMetrics {
  const entries: DistrictRiskEntry[] = Object.entries(data).map(([district, stats]) => ({
    district,
    individualsAtRisk: stats.total_individuals_at_risk,
    householdsAtRisk: stats.total_households_at_risk,
    band: getRiskBand(stats.total_individuals_at_risk),
  }));

  const bandCounts: Record<RiskBand, number> = { extreme: 0, high: 0, medium: 0, low: 0 };
  let districtsAtRiskCount = 0;
  let totalHouseholdsAtRisk = 0;
  let totalIndividualsAtRisk = 0;

  for (const entry of entries) {
    bandCounts[entry.band] += 1;
    if (entry.individualsAtRisk > 0) districtsAtRiskCount += 1;
    totalHouseholdsAtRisk += entry.householdsAtRisk;
    totalIndividualsAtRisk += entry.individualsAtRisk;
  }

  const top5 = [...entries]
    .sort((a, b) => b.individualsAtRisk - a.individualsAtRisk)
    .slice(0, MAX_TOP_DISTRICTS);

  return {
    districtsAtRiskCount,
    bandCounts,
    totalHouseholdsAtRisk,
    totalIndividualsAtRisk,
    top5,
  };
}